/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import useSWR, { KeyedMutator } from 'swr';
import isNonProduction from 'utilities/isNonProduction';
import AddressWithFirstAndLastName from '../types/AddressWithFirstAndLastName';
import FinalizeOrderResponse from '../types/FinalizeOrderResponse';
import Invoice from '../types/Invoice';
import { PaymentResponse } from '../types/PaymentResponse';
import uuid from '../utilities/uuid';

export const baseUrl = process.env.REACT_APP_SHOPPING_API_BASE_URL as string;
export const apiVersion = 'v7.1';

/**
 * Structured error type for API fetch failures
 * Used by SWR to provide detailed error information
 */
export interface FetchError extends Error {
  statusCode?: number;
  info?: {
    message: string;
  };
}

/**
 * Read a response body without throwing. 5xx responses from the Shopping API
 * can have an empty or non-JSON body, and calling `response.json()` on those
 * throws, which previously sent failed payments down the "request threw" path.
 */
export async function readJsonSafely<T>(response: Response): Promise<T | undefined> {
  try {
    const text = await response.text();
    return text ? (JSON.parse(text) as T) : undefined;
  } catch (e) {
    return undefined;
  }
}

/** Pull the backend trace id out of a ProblemDetails-style error body. */
export function getTraceId(body: unknown): string | undefined {
  if (body && typeof body === 'object' && 'traceId' in body) {
    const { traceId } = body as { traceId?: unknown };
    return typeof traceId === 'string' ? traceId : undefined;
  }
  return undefined;
}

function reportApiFailure(
  operation: string,
  status: number,
  message: string,
  context: { url: string; invoiceId?: string; body?: unknown }
): void {
  const level = status >= 500 ? 'warning' : 'error';
  Sentry.captureMessage(`${level} - ${status}: ${operation}${message}`, {
    level,
    fingerprint: [operation, String(status)],
    tags: {
      'api.operation': operation,
      'api.status': String(status),
      ...(context.invoiceId ? { invoiceId: context.invoiceId } : {}),
      ...(getTraceId(context.body) ? { 'api.trace_id': getTraceId(context.body) } : {}),
    },
    extra: { url: context.url, responseBody: context.body },
  });
}

function createFetchError(message: string, statusCode: number, body: unknown): FetchError {
  const error = new Error(message) as FetchError;
  error.statusCode = statusCode;
  error.info = body as FetchError['info'];
  return error;
}

async function fetcher<TData>(
  input: RequestInfo,
  init?: RequestInit
): Promise<TData> {
  const res = await fetch(input, init);

  if (!res.ok) {
    const error = new Error(
      'An error occurred while fetching the data.'
    ) as FetchError;

    const body = await readJsonSafely<FetchError['info']>(res.clone());
    error.info = body ?? { message: 'Failed to parse error response' };
    error.statusCode = res.status;

    const url = typeof input === 'string' ? input : input.url;
    reportApiFailure('SWR fetcher', res.status, '', {
      url,
      invoiceId: url.split('/invoices/')[1]?.split(/[/?]/)[0],
      body,
    });

    throw error;
  }

  return res.json();
}

// Maximum number of retries (not counting the first request) for transient
// (5xx / network) invoice-load failures. 4xx is never retried. SWR's default
// retries forever, and each retry used to raise another Sentry event.
export const INVOICE_ERROR_RETRY_COUNT = 3;

export function useGetInvoice(invoiceId: string): {
  invoice: Invoice | undefined;
  isLoading: boolean;
  isError: FetchError | undefined;
  mutateInvoice: KeyedMutator<Invoice>;
} {
  const { data, error, mutate } = useSWR<Invoice, FetchError>(
    `${baseUrl}/${apiVersion}/invoices/${invoiceId}`,
    fetcher,
    {
      // How the retry count advances: this callback never increments
      // `retryCount` itself. SWR does. Each time a fetch fails, SWR calls
      // onErrorRetry with `retryCount: (previous retryCount || 0) + 1`, where
      // "previous" is the value we handed back via `revalidate({ retryCount })`.
      // So the value here is 1 after the first failure, 2 after the first
      // retry fails, and so on. Passing it back unchanged is what lets SWR
      // keep counting.
      //
      // With INVOICE_ERROR_RETRY_COUNT = 3 and SWR's default
      // errorRetryInterval (5s), a persistent 5xx gives: initial request,
      // retry after 5s, retry after 10s, retry after 20s, then stop.
      // That is 4 requests at most.
      onErrorRetry: (err, _key, config, revalidate, { retryCount }) => {
        if (err.statusCode && err.statusCode >= 400 && err.statusCode < 500) return;
        if (retryCount > INVOICE_ERROR_RETRY_COUNT) return;
        setTimeout(
          () => revalidate({ retryCount }),
          config.errorRetryInterval * 2 ** (retryCount - 1)
        );
      },
    }
  );

  return {
    invoice: data,
    isLoading: !error && !data,
    isError: error,
    mutateInvoice: mutate,
  };
}

export async function payInvoice(
  invoiceId: string,
  paymentToken: string,
  billingAddress: AddressWithFirstAndLastName | null
): Promise<PaymentResponse> {
  const response = await fetch(
    `${baseUrl}/${apiVersion}/invoices/${invoiceId}/pay`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentToken,
        overrideAmount: null,
        billingAddress,
        requestId: uuid(),
        utcOffsetInMinutes: -new Date().getTimezoneOffset(),
      }),
    }
  );

  const body = await readJsonSafely<PaymentResponse>(response);

  if (!response.ok) {
    reportApiFailure('payInvoice', response.status, ` - ${response.statusText}`, {
      url: response.url,
      invoiceId,
      body,
    });
  }

  // A non-OK response may carry no body (or a ProblemDetails body with no
  // isSuccess). Either way it is not a success; the caller decides what the
  // user sees after checking whether the charge actually landed.
  return body ?? { isSuccess: false };
}

export async function finalizeOrder(
  invoiceId: string,
  customerPhoneNumber: string,
  shippingAddress: AddressWithFirstAndLastName | null,
  billingAddress: AddressWithFirstAndLastName | null
): Promise<FinalizeOrderResponse> {
  // The time-zone offset is the difference, in minutes, from local time to UTC.
  // Note that this means that the offset is positive if the local timezone is behind UTC,
  // and negative if it is ahead.
  const utcOffsetInMinutes = -new Date().getTimezoneOffset();

  const payload = {
    customerPhoneNumber,
    shippingAddress,
    billingAddress,
    utcOffsetInMinutes,
  };

  if (isNonProduction) {
    console.log(
      `finalizeOrder payload from ShoppingApi: ${JSON.stringify(
        payload,
        null,
        2
      )}`
    );
  }

  const response = await fetch(
    `${baseUrl}/${apiVersion}/invoices/${invoiceId}/finalize`,
    {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  const responseBody = (await readJsonSafely<FinalizeOrderResponse>(response)) ?? {};

  if (!response.ok) {
    reportApiFailure(
      'finalizeOrder',
      response.status,
      ` - ${JSON.stringify(responseBody)}`,
      { url: response.url, invoiceId, body: responseBody }
    );
    // Throw so callers take their error path. Returning the error body used to
    // look like a success: Billing moved on to /pay with no payment form and
    // the user was bounced straight back to Billing with no message.
    throw createFetchError('finalizeOrder failed', response.status, responseBody);
  }

  return responseBody;
}

/**
 * True for a FetchError raised after a non-OK API response. Those have already
 * been reported to Sentry by `reportApiFailure`, so callers should not report
 * them again.
 */
export function isReportedApiError(error: unknown): boolean {
  return (
    error instanceof Error &&
    typeof (error as FetchError).statusCode === 'number'
  );
}

const ShoppingApi = { useGetInvoice, payInvoice, finalizeOrder };

export default ShoppingApi;
